import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Coins, CornerDownRight, MessageCircle, Send } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { ClapIcon } from "../../lib/icons";

// FeedCommentsPanel — right-column comments panel for the home feed
// overlay. Mirrors ProfilePage's right-column layout so home + profile
// pages look identical when the player is active.
//
// Houses: applause button, bookmark (Save-to-Trail), comments scroll
// list (with threaded replies — single level deep, matching the
// backend contract in routers/comments.py), comment composer,
// sign-in nudge for guests.

// CommentItem — renders one top-level comment + its replies + a
// per-comment reply composer that can be toggled open/closed. Local
// state lives here so each thread can have its own open/closed state
// without lifting it up to the panel.
const CommentItem = ({
  comment,
  user,
  handleClosePlayer,
  handleSubmitReply,
  setShowSignInPrompt,
  setPendingAction,
}) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Collapse the replies list when there are more than 2 replies so the
  // thread doesn't crowd out other top-level comments. Two replies render
  // inline (cheap visual signal of activity); the 3rd+ hide behind a pill
  // the viewer can tap to expand.
  const replies = Array.isArray(comment.replies) ? comment.replies : [];
  const COLLAPSE_THRESHOLD = 2;
  const [showAllReplies, setShowAllReplies] = useState(replies.length <= COLLAPSE_THRESHOLD);

  const canAfford = !user || user.credits >= 0.20;

  const onReplyClick = () => {
    if (!user) {
      setShowSignInPrompt(true);
      setPendingAction('comment');
      return;
    }
    setOpen(o => !o);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() || submitting) return;
    setSubmitting(true);
    const result = await (handleSubmitReply ? handleSubmitReply(comment.id, text) : { ok: false });
    setSubmitting(false);
    if (result && result.ok) {
      setText("");
      setOpen(false);
      // Always show all replies after the viewer posts their own — they
      // expect to see what they just submitted.
      setShowAllReplies(true);
    }
  };

  const hiddenCount = Math.max(0, replies.length - COLLAPSE_THRESHOLD);
  const visibleReplies = showAllReplies ? replies : replies.slice(0, COLLAPSE_THRESHOLD);

  return (
    <div className="text-[11px]" data-testid={`feed-comment-${comment.id}`}>
      <div>
        <Link
          to={`/${comment.username}`}
          className="text-emerald-400 font-medium hover:underline"
          onClick={handleClosePlayer}
        >
          @{comment.username}
        </Link>
        <span className="text-gray-500 ml-1.5 text-[10px]">
          {new Date(comment.created_at).toLocaleDateString()}
        </span>
        <p className="text-gray-300 leading-snug">{comment.content || comment.text}</p>
        <button
          type="button"
          onClick={onReplyClick}
          className="mt-0.5 text-[10px] text-gray-500 hover:text-emerald-400 transition-colors"
          data-testid={`feed-comment-reply-btn-${comment.id}`}
        >
          {open ? 'Cancel' : 'Reply'}
        </button>
      </div>

      {replies.length > 0 && (
        <ul className="mt-1.5 pl-3 border-l border-gray-800 space-y-1.5" data-testid={`feed-comment-replies-${comment.id}`}>
          {visibleReplies.map((r) => (
            <li key={r.id} className="text-[11px]" data-testid={`feed-comment-reply-${r.id}`}>
              <div className="flex items-center gap-1 text-gray-500 text-[10px]">
                <CornerDownRight className="h-3 w-3" />
                <Link
                  to={`/${r.username}`}
                  className="text-emerald-400 font-medium hover:underline"
                  onClick={handleClosePlayer}
                >
                  @{r.username}
                </Link>
                <span>{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <p className="text-gray-300 leading-snug pl-4">{r.content || r.text}</p>
            </li>
          ))}
          {!showAllReplies && hiddenCount > 0 && (
            <li>
              <button
                type="button"
                onClick={() => setShowAllReplies(true)}
                className="inline-flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300 transition-colors"
                data-testid={`feed-comment-show-more-replies-${comment.id}`}
              >
                <CornerDownRight className="h-3 w-3" />
                Show {hiddenCount} more {hiddenCount === 1 ? 'reply' : 'replies'}
              </button>
            </li>
          )}
        </ul>
      )}

      {open && user && (
        <form
          onSubmit={onSubmit}
          className="mt-1.5 pl-3 border-l border-gray-800 flex gap-1.5"
          data-testid={`feed-comment-reply-form-${comment.id}`}
        >
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={canAfford ? `Reply to @${comment.username}…` : "Need 0.20 credits"}
            className="flex-1 bg-gray-800 border-gray-700 text-white text-[11px] h-7"
            disabled={submitting || !canAfford}
            data-testid={`feed-comment-reply-input-${comment.id}`}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!text.trim() || submitting || !canAfford}
            title={!canAfford ? "Need 0.20 credits — top up" : "Post reply (0.20 credits)"}
            data-testid={`feed-comment-reply-submit-${comment.id}`}
            className={`h-7 px-2 ${canAfford ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600/40 hover:bg-amber-600/60'}`}
          >
            {canAfford ? <Send className="h-3 w-3" /> : <Coins className="h-3 w-3" />}
          </Button>
        </form>
      )}
    </div>
  );
};

const FeedCommentsPanel = ({
  activeVideos,
  currentVideoIndex,
  user,
  comments,
  loadingComments,
  newComment,
  setNewComment,
  submittingComment,
  handleClosePlayer,
  handleApplaud,
  handleSubmitComment,
  handleSubmitReply,
  setShowSignInPrompt,
  setPendingAction,
  trailIds,
  handleToggleTrail,
}) => {
  const v = activeVideos[currentVideoIndex];

  return (
    <div className="w-full flex flex-col flex-1 min-h-0" data-testid="feed-comments-panel">
      {/* Header — comments-count + bookmark + applause pills on ONE line. */}
      <div className="p-3 flex items-center justify-between gap-2">
        <h4 className="text-gray-300 text-[11px] font-medium flex items-center gap-1.5">
          <MessageCircle className="h-3 w-3 text-cyan-400" />
          Comments ({comments.length})
        </h4>
        <div className="flex items-center gap-1.5">
            {(() => {
              if (!v) return null;
              const saved = !!(trailIds && trailIds.has && trailIds.has(v.id));
              const count = v.trail_count || 0;
              return (
                <button
                  onClick={handleToggleTrail}
                  data-testid="feed-trail-toggle"
                  data-saved={saved ? "true" : "false"}
                  aria-pressed={saved}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                    saved
                      ? 'bg-amber-500/25 hover:bg-amber-500/35 text-amber-300'
                      : 'bg-gray-700/50 hover:bg-gray-600/60 text-gray-300'
                  }`}
                  title={saved ? "Saved to your Trail — tap to remove" : "Save to your Trail"}
                >
                  <Bookmark className={`h-3 w-3 ${saved ? 'fill-amber-300' : ''}`} />
                  {count > 0 && <span className="tabular-nums">{count}</span>}
                </button>
              );
            })()}
            {(() => {
              const canAffordApplaud = !user || user.credits >= 0.50;
              return (
                <button
                  onClick={handleApplaud}
                  disabled={user && !canAffordApplaud}
                  data-testid="feed-comments-applaud"
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-all ${
                    canAffordApplaud
                      ? 'bg-pink-500/20 hover:bg-pink-500/30 text-pink-400'
                      : 'bg-amber-500/15 text-amber-400 cursor-not-allowed'
                  }`}
                  title={!canAffordApplaud ? "Need 0.50 credits — top up" : "Applaud this video (0.50 credits)"}
                >
                  {canAffordApplaud ? <ClapIcon className="h-3.5 w-3.5" /> : <Coins className="h-3 w-3" />}
                  <span className="tabular-nums">{v?.applaud_count || 0}</span>
                </button>
              );
            })()}
          </div>
      </div>

      {/* Scroll list — replies render inline beneath each parent. */}
      <ScrollArea className="flex-1 min-h-[160px] px-3 py-2">
        {loadingComments ? (
          <div className="flex justify-center py-3">
            <div className="animate-spin h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : comments.length > 0 ? (
          <div className="space-y-2">
            {comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                user={user}
                handleClosePlayer={handleClosePlayer}
                handleSubmitReply={handleSubmitReply}
                setShowSignInPrompt={setShowSignInPrompt}
                setPendingAction={setPendingAction}
              />
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-[11px] text-center py-3">No comments yet</p>
        )}
      </ScrollArea>

      {/* Composer */}
      <div className="px-3 py-2 border-t border-gray-800">
        {user ? (() => {
          const canAffordComment = user.credits >= 0.20;
          return (
            <form onSubmit={handleSubmitComment} className="flex gap-1.5">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={canAffordComment ? "Add a comment..." : "Need 0.20 credits"}
                className="flex-1 bg-gray-800 border-gray-700 text-white text-[11px] h-8"
                disabled={submittingComment || !canAffordComment}
              />
              <Button
                type="submit"
                size="sm"
                disabled={!newComment.trim() || submittingComment || !canAffordComment}
                title={!canAffordComment ? "Need 0.20 credits — top up" : "Post comment (0.20 credits)"}
                data-testid="feed-comment-submit"
                className={`h-8 px-2 ${canAffordComment ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600/40 hover:bg-amber-600/60'}`}
              >
                {canAffordComment ? <Send className="h-3 w-3" /> : <Coins className="h-3 w-3" />}
              </Button>
            </form>
          );
        })() : (
          <button
            type="button"
            onClick={() => {
              setShowSignInPrompt(true);
              setPendingAction('comment');
            }}
            className="w-full text-gray-500 hover:text-emerald-400 text-[11px] py-1.5 transition-colors"
            data-testid="feed-comment-signin-cta"
          >
            Sign in to comment →
          </button>
        )}
      </div>
    </div>
  );
};

export default FeedCommentsPanel;
